import * as React from 'react';
import { useState, useEffect, useRef } from 'react';

// Models from typing - Should be moved to types and generated from backend
export interface SearchResultResponse {
  totalCount: number;
  searchTerm: string;
  results: SearchResultItem[];
}

export interface SearchResultItem {
  title: string;
  price: string;
  url: string;
  imagePreview: string;
  sku: string;
  variants: number;
  inStock: boolean;
}

// --------------------------------------

interface IsearchBarProp {
  searchTerm: string;
  placeHolder: string;
  handleSearchTermChange: (inputVal: string) => void;
  handleSearchClear: () => void;
  showAllSearchResults: (searchTerm: string) => void;
}

interface IsearchResultListProps {
  response: SearchResultResponse;
  hasMore: boolean;
  setHasMore: (isSet: boolean) => void;
  handleSearchClear: () => void;
  listRef: React.RefObject<HTMLDivElement>;
  showAllSearchResults: (searchTerm: string) => void;
}

export interface ISearchRequest {
  searchTerm: string;
}

const Image = (props: {src?: string; alt?: string}) => {
  return props.src
    ? <img src={props.src} loading='lazy' alt={props.alt ?? ''} />
    : <i className='fa-regular fa-box'></i>;
};

const SearchBar = (props: IsearchBarProp) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      props.showAllSearchResults(props.searchTerm);
    }
  };

  return (
    <div className='search-bar-wrapper'>
      <div className='input-container'>
        <input
          className='search-input'
          placeholder={props?.placeHolder ?? 'Jag vill köpa...'}
          value={props.searchTerm}
          onChange={(e) => props.handleSearchTermChange(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        {props.searchTerm && (
          <button className='clear-icon search-clear' onClick={props.handleSearchClear}>
            <i className='fa-solid fa-xmark' />
          </button>
        )}
      </div>
      <button className='search-button' onClick={() => props.showAllSearchResults(props.searchTerm)}>
        <i className='fa-regular fa-magnifying-glass'></i>
      </button>
    </div>
  );
};

const SearchResult = (props: SearchResultItem) => {
  return (
    <a href={props.url} className='search-result'>
      <div className='content'>
        <p className='title'>{props.title}</p>
        {props.variants < 2 && (
          <>
            <p className='info'>{props.sku}</p>
            {props.inStock &&
              <p className='info'>
                <i className='fa-regular fa-box in-stock'></i> I lager
              </p>
            }
            {!props.inStock &&
              <p className='info'>
                <i className='fa-regular fa-box not-in-stock'></i> Slut i lager
              </p>
            }
            <p className='info price'>{props.price}</p>
          </>
        )}
        {props.variants > 1 && (
          <>
            <p className='info'>
              {' '}
              Från <span className='price'>{props.price}</span>
            </p>
          </>
        )}
      </div>
      <div className='product-image'>
        <p>
          <Image src={props.imagePreview} alt={props.title}/>
        </p>
      </div>
    </a>
  );
};

const SearchResultsList = (props: IsearchResultListProps) => {
  const handleClickOutside = (event: MouseEvent) => {
    if (props.listRef.current && !props.listRef.current.contains(event.target as Node)) {
      props.handleSearchClear();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className='result-list'>
      <p className='total-count'>{props.response.totalCount} search results</p>
      {props.response.results.length > 0 &&
              props.response.results.map(
                (searchItem: SearchResultItem, id: number) => {
                  return <SearchResult key={id} {...searchItem} />;
                })}
      {props.hasMore &&
        <div className='empori-button' onClick={() => props.showAllSearchResults(props.response.searchTerm)}>
          Show all({props.response.totalCount})
        </div>
      }
    </div>
  );
};

const NoResult = () => {
  return (
    <div className='result-list no-search-result'>
      <i className='fa-regular fa-magnifying-glass'></i>
      Inga resultat
    </div>
  );
};

export default function Search() {
  const [response, setResults] = useState<SearchResultResponse>({
    totalCount: 0,
    searchTerm: '',
    results: []
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [timer, setTimer] = useState<NodeJS.Timeout>();
  const [hasMore, setHasMore] = useState(true);

  const fetchData = (value: string) => {
    const searchReq: ISearchRequest = {
      searchTerm: value
    };

    fetch('/umbraco/EmporiApi/PrimeflowSearch/Search', {
      method: 'post',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-type': 'application/json; charset=UTF-8'
      },
      body: JSON.stringify(searchReq)
    })
      .then((res) => res.json())
      .then((json) => {
        setHasMore(json.totalCount > 4);
        setResults(json);
      });
  };

  const handleChange = async (inputVal: string) => {
    setSearchTerm(inputVal);
    if (inputVal) {
      if (timer) {
        clearTimeout(timer);
      }

      const timeOutIdentifier = setTimeout(() => {
        fetchData(inputVal);
      }, 500);
      setTimer(timeOutIdentifier);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setResults({
      totalCount: 0,
      searchTerm: '',
      results: []
    });
  };

  const showAllSearchResults = (term: string) => {
    window.location.href = `/butik/search?q=${term}&mode=text`;
  };

  const listRef = useRef<HTMLDivElement>(null);

  return (
    <div className='empori-primeflow-search' ref={listRef}>
      <SearchBar
        searchTerm={searchTerm}
        placeHolder={'Jag vill köpa...'}
        handleSearchTermChange={handleChange}
        handleSearchClear={clearSearch}
        showAllSearchResults={showAllSearchResults}
      />

      {searchTerm &&
        response &&
        (response.totalCount > 0 ? (
          <SearchResultsList
            response={response}
            setHasMore={setHasMore}
            hasMore={hasMore}
            handleSearchClear={clearSearch}
            listRef={listRef}
            showAllSearchResults={showAllSearchResults}
          />
        ) : (
          <NoResult />
        ))}
    </div>
  );
}